import { useEffect, useState } from "react";

/**
 * Below this width the project bar takes two rows (13.11, iPhone portrait,
 * ruled 2026-10-21). Measured: the one-row bar needs ~500px, so a phone held
 * upright (390–430) and a narrow iPad window (Slide Over, a third of Split
 * View) lost More options and Notifications off the right edge. A phone on
 * its side (844+) and every iPad full screen stay on one row.
 */
export const NARROW_BAR_MAX = 599;

/**
 * Below this width the one-row bar folds Refresh, My Stuff and Sharing into
 * More options, as the two-row bar does. A small phone on its side (the SE at
 * 667) kept them all in the row and squeezed the title to 5pt (Phase 12,
 * 2026-10-22); the 844 phones and every iPad full screen are unchanged.
 */
export const FOLD_BAR_MAX = 759;

/** True while the media query matches; follows rotation and resizes. */
export function useMedia(query: string): boolean {
  const [hit, setHit] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setHit(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return hit;
}

/** True while the window is at most `px` wide; follows rotation and resizes.
 *  Compact layouts share NARROW_BAR_MAX, the phone-upright line. */
export function useMaxWidth(px: number): boolean {
  return useMedia(`(max-width: ${px}px)`);
}

/**
 * A phone on its side: wider than tall and at most 500px tall. The world's bar
 * becomes a left rail here (operator, 2026-10-29: "love the new world top bar on
 * phones"), giving the world back the bar's height. Every iPad (820+ tall in
 * landscape) and a desktop window of ordinary height never match.
 */
export const SHORT_LANDSCAPE_QUERY = "(orientation: landscape) and (max-height: 500px)";

export function useShortLandscape(): boolean {
  return useMedia(SHORT_LANDSCAPE_QUERY);
}

export function useNarrowBar(): boolean {
  return useMaxWidth(NARROW_BAR_MAX);
}

/** True when the bar's shortcuts live in More options (always so when narrow). */
export function useFoldBar(): boolean {
  return useMaxWidth(FOLD_BAR_MAX);
}
